/**
 * duplicateSurface.tsx — the DUPLICATE pair, readable from BOTH ends
 * (2026-09-06, CTO: the Asana model).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHAT WAS WRONG WITH WHAT WE HAD. clinic-service has stored
 * `Deal.duplicateOfDealId` for a while and the portal rendered none of it: a
 * merged card looked like an ordinary closed card, the survivor said nothing at
 * all, and the only record of the merge was two audit rows whose de-underscored
 * action names ("deal marked duplicate — marked as duplicate") sat unlabelled
 * in the Audit-log tab. A ONE-WAY POINTER NOBODY CAN SEE IS NOT A RELATIONSHIP.
 * The CTO's brief is four things and they are all about reachability:
 *
 *   a) the merged card carries a visible `[Duplicate]` marker;
 *   b) it says "Closed and merged into …" as a banner, with a working link;
 *   c) the CANONICAL lists what was merged into it — bidirectional;
 *   d) BOTH activity trails carry the link.
 *
 * ⚠️ AND THE MONEY RULE HOLDS OVER ALL FOUR. A duplicate with a PAID order is a
 * patient who submitted twice and was CHARGED twice. Marking a duplicate
 * deliberately cascades to nothing in orders-service — unlike the test mark,
 * which sets `OrderCase.isTest` and takes the case off the payments board — so
 * the charge stays exactly where an admin can refund it. Nothing in this module
 * may hide or soften that: the `[Duplicate]` marker is muted, the closed banner
 * is `info`, and the ONLY danger-toned thing here is the money. A merged card
 * that de-emphasised a real charge would be the feature causing the harm it
 * exists to prevent.
 *
 * R41 — THE SERVER IS THE CONTROL. Every renderer below is gated on a server
 * FACT (`duplicateOfDealId`, `mergedDuplicates`, `paymentReviewRequired`) and an
 * absent field renders NOTHING. The portal writes the sentences and the hrefs;
 * clinic-service deliberately ships no label to keep in step with them.
 *
 * R19 — no PHI here. A merged duplicate is the same person as the canonical, so
 * the server sends no contact block with these rows and this module identifies
 * them by their JOURNEY (entry path, dates, stage) rather than re-printing a
 * patient. `mergedByUserId` is an opaque principal; the name, when there is one,
 * comes from the drawer's ADMIN-only batched staff lookup, never from the wire.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { DealActivityEntry, DealCard, DealMergedDuplicate } from '../api/types';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { Alert, Badge } from '../ui/kit';
import { ENTRY_LABEL, STAGE_LABEL } from './stages';
import { toneFor } from '../ui/tones';

/* ── The facts, as predicates ──────────────────────────────────────────────
 *
 * `!= null` and `=== true` throughout, never truthiness: an ABSENT field is a
 * clinic-service that predates the feature and must read as "this server does
 * not report duplicates", not as "this card is not one".
 */

/** This card was merged away into another one. */
export function isMergedDuplicate(card: Pick<DealCard, 'duplicateOfDealId'>): boolean {
  return card.duplicateOfDealId != null;
}

/**
 * ⚠️ This card is a duplicate AND carries a real charge — the double-charge
 * flag. Server-derived; the portal never recomputes it from `orderCaseRef`,
 * because the two would then disagree the moment the server's rule moves.
 */
export function needsPaymentReview(card: Pick<DealCard, 'paymentReviewRequired'>): boolean {
  return card.paymentReviewRequired === true;
}

/** The merged duplicates that carry a charge — the rows an admin must act on. */
export function paidMergedDuplicates(rows: readonly DealMergedDuplicate[]): DealMergedDuplicate[] {
  return rows.filter((row) => row.paymentReviewRequired);
}

/* ── (a) THE MARKER ────────────────────────────────────────────────────────── */

const DUPLICATE_MARKER_LABEL = 'Duplicate — this journey was merged into another deal';

/**
 * The `[Duplicate]` marker, worn beside the contact's name on the board card
 * and in the drawer header.
 *
 * LITERALLY BRACKETED, AND THAT IS THE ASANA MODEL, NOT A TYPOGRAPHIC ACCIDENT.
 * Asana prefixes a merged task's title, and the reason it works is that the
 * marker travels WITH THE NAME: wherever the card is quoted — a lane, a
 * drawer header, a screenshot pasted into a ticket — the brackets come too. A
 * chip in the tag row would not, and the tag row is also where every clinical
 * cue lives, so a bookkeeping mark there competes with the facts a clinician is
 * scanning for.
 *
 * NOT the test record's strike-through, deliberately. A test record is VOID
 * DATA — nothing on it happened — and the strike says so. A merged duplicate
 * really happened to a real patient, and may have taken their money; striking
 * it out would tell staff to disregard the one card that might need a refund.
 *
 * Muted, never a warning tone: see the money rule in the module header.
 */
export function DuplicateMarker({ style }: { style?: CSSProperties }) {
  return (
    <span
      role="img"
      aria-label={DUPLICATE_MARKER_LABEL}
      title={DUPLICATE_MARKER_LABEL}
      style={{ fontWeight: 700, fontSize: '12px', color: 'var(--gps-muted)', whiteSpace: 'nowrap', ...style }}
    >
      [Duplicate]
    </span>
  );
}

/* ── The in-app link to the other card ─────────────────────────────────────
 *
 * ⚠️ A DEAL HAS NO URL IN THIS PORTAL, so "a working link" cannot be an
 * `<a href>`. The board opens a drawer by handing `PipelineTab` a whole
 * `DealCard` (`setSelected`), and a merged card is HIDDEN from the default
 * board read, so it is not in the loaded page to hand over — which is exactly
 * why the pointer has been unreachable. The owner therefore takes a dealId and
 * fetches what it needs (see `DealDrawer`'s `onOpenDeal` and PipelineTab's
 * `openDealById`), and this is the affordance that calls it.
 *
 * A REAL `<button>` styled as a link, not an anchor: it performs an in-app
 * navigation, and an anchor with `href="#"` would offer a copyable address that
 * goes nowhere and would break middle-click and "open in new tab" in a way a
 * button never promises. It keeps its focus outline.
 */
export function DealLinkButton({
  onClick,
  children,
  label,
}: {
  onClick: () => void;
  children: ReactNode;
  /** The accessible name, when the visible words need more context. */
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: 'var(--gps-link)',
        cursor: 'pointer',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      }}
    >
      {children}
    </button>
  );
}

/* ── (b) THE MERGED CARD'S BANNER ──────────────────────────────────────────── */

const LINE: CSSProperties = { display: 'block' };
const LEAD: CSSProperties = { ...LINE, fontWeight: 700 };

/**
 * "Closed and merged into another deal" — the first thing on a merged card.
 *
 * ABOVE THE TAB STRIP, beside the action-needed banner, and NOT inside the
 * Overview panel where the closure outcome block sits. The reason is the same
 * one that put the action banner there: whatever tab the drawer opens on, the
 * reader has to learn this without first knowing where to look. And here it is
 * stronger — this banner's message is "you are on the wrong card", so a
 * placement that only pays off on one tab is a placement that lets somebody
 * work a dead record on the other five.
 *
 * `info`, not `danger`. The merge is normal housekeeping and the card is not
 * broken; the only thing on a duplicate that deserves alarm is a charge, and
 * that has its own banner below. Colour is never alone (WCAG 1.4.1) — the words
 * carry the whole message.
 *
 * NO NAME FOR THE OTHER CARD. The wire gives this side of the pair an ID and
 * nothing else (R41/R19: no contact block, no server-side label), so the link
 * says what it does rather than pretending to know whose deal it opens. The
 * canonical's own side of the relationship — dates, who merged it, the money
 * flag — is the list below, which is where the detail belongs.
 */
export function MergedIntoBanner({ onOpenCanonical }: { onOpenCanonical?: () => void }) {
  return (
    <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <span style={LEAD}>Closed and merged into another deal</span>
      <span style={{ ...LINE, marginTop: '4px' }}>
        This is a second copy of the same patient&rsquo;s journey. Work the deal it was merged into
        &mdash; nothing recorded here moves them forward.
        {onOpenCanonical ? (
          <>
            {' '}
            <DealLinkButton onClick={onOpenCanonical} label="Open the deal this one was merged into">
              Open that deal
            </DealLinkButton>
          </>
        ) : null}
      </span>
    </Alert>
  );
}

/* ── THE MONEY, ON BOTH SIDES ──────────────────────────────────────────────── */

/**
 * ⚠️ THIS MERGED CARD WAS PAID — shown on the DUPLICATE's own drawer.
 *
 * The failure this exists to prevent: a card gets merged, the board stops
 * showing it, and a real charge on a real patient goes quiet with it. Marking a
 * duplicate cascades to NOTHING in orders (deliberately — see the module
 * header), so the order is still live on the ADMIN payments boards and still
 * refundable; the risk is nobody looking. `danger` + `role="alert"`, which is
 * the only assertive thing this module renders.
 *
 * SHOWN TO BOTH ROLES, and it names no figure. A clinician may not action a
 * refund and never sees the payments board, but they are frequently the person
 * who spots the double submission — telling them "there is a payment on this,
 * raise it with admin" is the whole ask, and it needs no amount to be actionable.
 * Keeping it money-word-free also keeps the clinician surface clean for the
 * money-redaction sweep in clinicians.spec.tsx.
 */
export function DuplicatePaymentBanner() {
  return (
    <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <span style={LEAD}>This merged deal has an order behind it</span>
      <span style={{ ...LINE, marginTop: '4px' }}>
        The patient may have been charged twice. Merging a deal does not touch the order &mdash; it is
        still live and still refundable. Check it on the admin payments board and refund it if it is a
        double charge.
      </span>
    </Alert>
  );
}

/**
 * ⚠️ THE SAME WARNING, FROM THE SURVIVING SIDE — shown on the CANONICAL when
 * any of the deals merged into it carries an order.
 *
 * THIS IS THE ONE THAT ACTUALLY GETS READ, and it is the reason the canonical
 * needed a list at all. The duplicate's own drawer is a card the board hides;
 * the canonical is the card staff keep open. If a double charge is only visible
 * from the record nobody opens, it is not visible.
 */
export function AbsorbedPaymentBanner({ count }: { count: number }) {
  return (
    <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
      <span style={LEAD}>
        {count === 1
          ? 'A deal merged into this one has an order behind it'
          : `${count} deals merged into this one have orders behind them`}
      </span>
      <span style={{ ...LINE, marginTop: '4px' }}>
        The patient may have been charged more than once. Merging does not touch an order &mdash; each is
        still live and still refundable. Check them on the admin payments board.
      </span>
    </Alert>
  );
}

/* ── (c) THE CANONICAL'S LIST ──────────────────────────────────────────────── */

const rowStyle: CSSProperties = {
  border: '1px solid var(--gps-border)',
  borderRadius: 'var(--gps-r-md)',
  padding: '8px 12px',
  display: 'grid',
  gap: '4px',
};

const metaStyle: CSSProperties = { fontSize: '13px', color: 'var(--gps-muted)' };

/**
 * WHAT WAS MERGED INTO THIS CARD — the other half of the pair, as rows.
 *
 * WHY ROWS AND NOT THE `mergedDuplicateDealIds` THE CARD ALREADY CARRIES. A
 * bare UUID answers no question a human has. These rows answer the three that
 * matter on sight: WHEN the other journey ran (so a clinician can see there was
 * a second submission and roughly when), WHO merged it and when (accountability
 * for a staff action that removes a card from the board), and WHETHER IT WAS
 * PAID.
 *
 * THE ROW IS ORDERED BY WHAT A READER NEEDS FIRST: the money flag leads,
 * because it is the only line here that demands an action.
 *
 * `mergedAt` and `mergedByUserId` are BOTH independently nullable and are
 * rendered independently: a pointer set before those columns existed has the
 * association and no stamp, and saying "merged" with no attribution is honest
 * where inventing one would not be. A `mergedByUserId` the ADMIN lookup cannot
 * resolve renders NO name rather than a raw UUID — a UUID in a "who did this"
 * slot reads to staff as a system action.
 */
export function MergedDuplicatesList({
  rows,
  actorNames,
  onOpenDeal,
}: {
  rows: readonly DealMergedDuplicate[];
  /** userId → display name, from the drawer's ADMIN-only batched lookup. */
  actorNames?: Record<string, string | undefined>;
  onOpenDeal?: (dealId: string) => void;
}) {
  return (
    <ul
      aria-label="Deals merged into this one"
      style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '8px' }}
    >
      {rows.map((row) => {
        const mergedBy = row.mergedByUserId ? actorNames?.[row.mergedByUserId] : undefined;
        return (
          <li key={row.dealId} style={rowStyle}>
            {row.paymentReviewRequired ? (
              // Per-row, not just the banner above: with several merges the
              // banner says HOW MANY are paid and this says WHICH.
              <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--gps-danger-text)' }}>
                Has an order &mdash; check for a double charge
              </span>
            ) : null}
            <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
              <DuplicateMarker />
              <Badge tone={toneFor(row.stage)}>{STAGE_LABEL[row.stage] ?? row.stage}</Badge>
              <span style={metaStyle}>{ENTRY_LABEL[row.entryPath] ?? row.entryPath}</span>
              {onOpenDeal ? (
                <DealLinkButton
                  onClick={() => onOpenDeal(row.dealId)}
                  label={`Open the merged deal started on ${formatUtcDate(row.createdAt)}`}
                >
                  Open
                </DealLinkButton>
              ) : null}
            </span>
            <span style={metaStyle}>
              Started {formatUtcDate(row.createdAt)}
              {/* Two independent facts, two independent clauses — see the
                  nullability note above. */}
              {row.mergedAt ? ` · merged ${formatUtcDateTime(row.mergedAt)}` : ' · merge date not recorded'}
              {mergedBy ? ` · by ${mergedBy}` : ''}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/* ── (d) BOTH ACTIVITY TRAILS ──────────────────────────────────────────────
 *
 * ⚠️ WHY THIS IS DONE BY MATCHING THE SUMMARY TEXT, WHICH IS UGLY, AND WHAT
 * WOULD REPLACE IT.
 *
 * clinic-service relabels a handful of audit actions into readable feed lines
 * (`AUDIT_FEED_LABELS`), but the two duplicate actions are NOT among them, and
 * the feed entry carries no `action` and no `context` — `dealActivityEntry` is
 * `{at, kind, actorUserId, actorRole, summary, outcome, body}` and nothing else.
 * So what reaches the portal for these rows is the generic rendering:
 * `<action with underscores stripped> — <reason>`, i.e. exactly
 *
 *     "deal marked duplicate — marked as duplicate"
 *     "deal marked duplicate — marked as duplicate: <staff note>"
 *     "deal marked duplicate — duplicate mark reverted"
 *     "deal duplicate absorbed — absorbed a duplicate of this deal"
 *     "deal duplicate absorbed — a duplicate of this deal was released"
 *
 * The prefix IS the action name, so it is a real handle rather than a guess at
 * prose — the same bargain `humaniseStageSummary` already strikes with the
 * `"<from> -> <to>"` seam. THE PROPER FIX IS TWO LINES IN clinic-service: add
 * both actions to `AUDIT_FEED_LABELS`, and (for the link) put the counterpart
 * deal id on the feed entry the way the audit ROW already carries it in
 * `context`. Until then, matching here is what lets the trail say what happened
 * instead of showing staff a de-underscored enum. If the action name ever
 * changes, this stops matching and the row falls back to the raw wire text —
 * degrading to today's behaviour, never to a wrong sentence.
 */

const MARK_PREFIX = 'deal marked duplicate';
const ABSORB_PREFIX = 'deal duplicate absorbed';
/** The server's two reason prefixes for the UNDO direction (`markDuplicateReason`
 *  / `absorbDuplicateReason` in clinic-service deals.service.ts). */
const REVERTED = 'duplicate mark reverted';
const RELEASED = 'a duplicate of this deal was released';
/** The mark's reason, and the separator before an optional staff note. */
const MARKED = 'marked as duplicate';

export type DuplicateTrailKind = 'marked' | 'reverted' | 'absorbed' | 'released';

export interface DuplicateTrailRow {
  kind: DuplicateTrailKind;
  /** The staff note that rode the mark, VERBATIM, or null. */
  note: string | null;
}

/**
 * Recognise one of the four duplicate audit rows in a feed summary, or null.
 *
 * DIRECTION IS READ FROM THE REASON, NOT ASSUMED. Both a mark and its undo are
 * written under the SAME audit action, so the action prefix alone cannot tell
 * "merged" from "un-merged" — and rendering an undo as a merge would tell staff
 * a live card had been closed. An unrecognised reason on a known action falls
 * back to the merge/absorb reading only when it says so; anything else returns
 * null and the row keeps its raw wire text.
 */
export function duplicateTrailRow(summary: string): DuplicateTrailRow | null {
  const [head, ...rest] = summary.split(' — ');
  const reason = rest.join(' — ').trim();
  const action = head.trim();
  if (action === MARK_PREFIX) {
    if (reason.startsWith(REVERTED)) return { kind: 'reverted', note: null };
    if (reason.startsWith(MARKED)) {
      // `markDuplicateReason` appends an optional staff note after ": ".
      const note = reason.slice(MARKED.length).replace(/^:\s*/, '').trim();
      return { kind: 'marked', note: note.length > 0 ? note : null };
    }
    return null;
  }
  if (action === ABSORB_PREFIX) {
    if (reason.startsWith(RELEASED)) return { kind: 'released', note: null };
    return { kind: 'absorbed', note: null };
  }
  return null;
}

/**
 * WHICH DEAL, IF ANY, A CANONICAL-SIDE "absorbed" ROW MAY LINK TO.
 *
 * ⚠️ THE HARD CASE, AND WHY THIS IS ALLOWED TO ANSWER "NO". On the DUPLICATE
 * side the answer is trivial: the card has exactly one `duplicateOfDealId`. On
 * the CANONICAL side it is not, because the feed entry carries no id and the
 * canonical can have absorbed several deals. Worse, the audit trail SURVIVES A
 * REVERT while the pointer does not — so a canonical showing one merged
 * duplicate today may have an older absorbed row belonging to a completely
 * different deal that was since released. Linking that row to the surviving
 * duplicate would put a wrong deal id on a clinical record.
 *
 * So the link is offered only when the pair is UNAMBIGUOUS: exactly one deal
 * currently points here, AND exactly one absorb-flavoured row exists in the
 * whole trail. One row, one pointer — they are the same event, and no revert
 * can have happened (a revert writes a second row). Anything else returns null
 * and the sentence stands on its own, with the full list in the Overview
 * section carrying every link.
 */
export function absorbedRowLink(opts: {
  mergedDealIds: readonly string[];
  absorbRowCount: number;
}): string | null {
  return opts.mergedDealIds.length === 1 && opts.absorbRowCount === 1 ? opts.mergedDealIds[0] : null;
}

/** How many absorb-flavoured rows a feed holds — the denominator above. */
export function countAbsorbRows(activity: readonly DealActivityEntry[]): number {
  return activity.filter((entry) => duplicateTrailRow(entry.summary)?.kind === 'absorbed').length;
}

/** The plain-English sentence for each recognised row. */
const TRAIL_COPY: Record<DuplicateTrailKind, string> = {
  marked: 'Marked as a duplicate and merged into another deal',
  reverted: 'Duplicate mark removed — this deal is back in the pipeline',
  absorbed: 'A duplicate deal was merged into this one',
  released: 'A duplicate was released from this deal and is back in the pipeline',
};

/**
 * The trail row's rendered content: the sentence, the staff note VERBATIM when
 * one rode the mark, and the link to the other card when there is one to give.
 *
 * The note is a plain TEXT NODE (React escapes it) — it is staff free text off
 * an audit row, not note HTML, so it never goes near
 * `dangerouslySetInnerHTML` (R19: staff reads only, never a log line).
 */
export function DuplicateTrailSummary({
  row,
  linkDealId,
  onOpenDeal,
}: {
  row: DuplicateTrailRow;
  /** The counterpart card, when it can be named without guessing. */
  linkDealId?: string | null;
  onOpenDeal?: (dealId: string) => void;
}) {
  const target = linkDealId ?? null;
  return (
    <span>
      {TRAIL_COPY[row.kind]}
      {row.note ? <span> &mdash; {row.note}</span> : null}
      {target && onOpenDeal ? (
        <>
          {' '}
          <DealLinkButton
            onClick={() => onOpenDeal(target)}
            label={row.kind === 'marked' ? 'Open the deal this one was merged into' : 'Open the deal that was merged in'}
          >
            {row.kind === 'marked' ? 'Open that deal' : 'Open the merged deal'}
          </DealLinkButton>
        </>
      ) : null}
    </span>
  );
}
